import { mq } from './breakpoints';
import { useMediaQuery } from './useMediaQuery';

/**
 * A data table's first column is pinned (`position: sticky` at the inline start) from `bp/tablet` up,
 * and not below it (design-system §9 Tables; BUG-232). At the 320px reflow the table's scroll region
 * is narrow, so a pinned column can be wider than the room it leaves: scrolled to its end, the last
 * column then sits under the pinned one, and its controls fall below the 24px target floor (axe
 * `target-size`). Unpinned, the table simply scrolls inside its own region, which SC 1.4.10 allows.
 *
 * A hook rather than a CSS `@media`, because the `bp/*` values live only in `./breakpoints` (FS-3
 * AC-6). The table carries the answer as `data-pin-first`, which every table keys on:
 *
 * - **shadcn tables** put `PIN_GROUP` and the attribute on the `<table>`, and give the pinned cells
 *   `PINNED_CELL` (and the header's corner cell `PINNED_CORNER`), so the cells read the table's
 *   attribute. The classes are written only here, so `src/ui/styles/shadcn.css` names this file as a
 *   Tailwind `@source`: without it the build generates none of them;
 * - **CSS-module tables** select on `[data-pin-first]` in their own stylesheet.
 */
export function usePinnedFirstColumn(): { readonly 'data-pin-first': '' | undefined } {
    return { 'data-pin-first': useMediaQuery(mq.tablet) ? '' : undefined };
}

/** The named Tailwind group on the `<table>` that the pinned cells read. */
export const PIN_GROUP = 'group/table';

/** A body cell of the first column: pinned at the inline start while the table pins its first column. */
export const PINNED_CELL = 'group-data-[pin-first]/table:sticky group-data-[pin-first]/table:start-0';

/**
 * The header's first cell, which stays at the top of the scroll region at every width (its own
 * `sticky top-0`) and is pinned at the inline start too while the table pins its first column.
 */
export const PINNED_CORNER = 'group-data-[pin-first]/table:start-0';
